import { CountryCode } from '@weave/schema-gen-ts/dist/schemas/forms-digital/weave_digital_forms.pb';
import { FormsV1 } from '@frontend/api-digital-forms';
import { ALLOWED_COUNTRY_CODES } from '../constants';
import { parseJsonOr } from '../helpers/json-helpers';
import {
  isPaymentFieldType,
  toPaymentDraftAnswer,
} from '../page-contents/filling-experience/components/dynamic-form-container/fields/payment-on-file-field/payment-field.helpers';
import { getSignedAttachmentId } from '../page-contents/filling-experience/components/dynamic-form-container/fields/pdf-field/pdf-value';
import { getFieldOptions } from '../page-contents/filling-experience/components/dynamic-form-container/helpers';
import {
  isESignSigned,
  isImageESign,
  parseESignValue,
} from '../page-contents/filling-experience/components/dynamic-form-container/signature/esign-value';
import { isPersistedMediaId } from '../page-contents/filling-experience/components/dynamic-form-container/signature/media-id';
import { NON_SUBMITTABLE_FIELD_TYPES } from '../page-contents/filling-experience/components/dynamic-form-container/submit-form.helpers';
import { DynamicFormField } from '../page-contents/filling-experience/components/dynamic-form-container/types';
import { DraftMetadata, DraftSnapshot } from './draft.types';

export const DRAFT_PAYLOAD_VERSION = 1;

/**
 * Field types never written to a draft: presentation-only fields, signature consent, and the date populator
 * (re-derived on load). Media fields (including eSign images and signed PDFs) persist committed server media ids
 * only - never file contents. Payment fields persist the payment method reference only (masked details are
 * re-fetched on restore).
 */
export const DRAFT_EXCLUDED_FIELD_TYPES = new Set<string>([
  ...NON_SUBMITTABLE_FIELD_TYPES,
  'signature',
  'currentDatePopulator',
]);

type SaveDraftRequest = FormsV1.Types.SaveDraftIO['input'];
type GetDraftResponse = FormsV1.Types.GetDraftIO['output'];

type DraftFieldsMap = Record<string, Pick<DynamicFormField, 'key' | 'meta' | 'value' | 'options'>>;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value) && Object.getPrototypeOf(value) === Object.prototype;

const isExcludedField = (key: string, fieldsMap: DraftFieldsMap): boolean => {
  const field = fieldsMap[key];
  if (!field) return true;
  if (DRAFT_EXCLUDED_FIELD_TYPES.has(field.meta.type)) return true;
  // Consent checkbox paired with a signature field (`<signatureKey>-checkbox`).
  if (key.endsWith('-checkbox')) {
    const owner = fieldsMap[key.slice(0, -'-checkbox'.length)];
    if (owner?.meta.type === 'signature') return true;
  }
  return false;
};

/**
 * Normalizes an answer into a JSON-safe value. Empty answers become `undefined` so that untouched (`undefined`)
 * and cleared (`''`/`[]`) inputs compare equal, while clearing a previously saved answer still removes its key.
 */
const normalizeAnswer = (value: unknown): unknown => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') return value === '' ? undefined : value;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'boolean') return value;
  if (Array.isArray(value)) {
    const items = value.map(normalizeAnswer).filter((item) => item !== undefined);
    return items.length ? items : undefined;
  }
  if (isPlainObject(value)) {
    const entries = Object.entries(value)
      .map(([key, item]) => [key, normalizeAnswer(item)] as const)
      .filter(([, item]) => item !== undefined);
    return entries.length ? Object.fromEntries(entries) : undefined;
  }
  // Files, blobs, dates, and other client-only objects are never persisted.
  return undefined;
};

/** Field types whose answers are arrays/objects; template defaults arrive JSON-encoded (e.g. `"[]"`). */
const STRUCTURED_FIELD_TYPES = new Set<string>([
  'checklist',
  'multiselect-dropdown',
  'categorizedChecklist',
  'imageUpload',
  'cardCapture',
  'address',
  'insuranceVerification',
  'cardOnFile',
  'achOnFile',
]);

/** Narrows a live value to what may be persisted for its type. */
const projectDraftValue = (type: string, value: unknown): unknown =>
  isPaymentFieldType(type) ? toPaymentDraftAnswer(type, value) : value;

const decodeFieldValue = (type: string, value: unknown): unknown => {
  if (type === 'eSign') {
    // Unsigned values aren't drafted; only the four persisted keys are kept.
    const signature = parseESignValue(value);
    if (!isESignSigned(signature)) return undefined;
    const { type: signatureType, data, font_type, timestamp } = signature;
    return { type: signatureType, data, font_type, timestamp };
  }
  if (type === 'pdf') {
    // Only a signed (uploaded) copy is drafted.
    const signedAttachmentId = getSignedAttachmentId(value);
    return isPersistedMediaId(signedAttachmentId) ? { signedAttachmentId } : undefined;
  }
  return STRUCTURED_FIELD_TYPES.has(type) && typeof value === 'string' ? parseJsonOr<unknown>(value, value) : value;
};

/** Captures an immutable, persistable snapshot of the current editing state. */
export const captureDraftSnapshot = ({
  formFieldsMap,
  activeSectionIndex,
  countryCode,
}: {
  formFieldsMap: DraftFieldsMap;
  activeSectionIndex: number;
  countryCode?: CountryCode;
}): DraftSnapshot => {
  const answers: Record<string, unknown> = {};

  Object.entries(formFieldsMap).forEach(([key, field]) => {
    if (isExcludedField(key, formFieldsMap)) return;
    const value = normalizeAnswer(projectDraftValue(field.meta.type, decodeFieldValue(field.meta.type, field.value)));
    if (value !== undefined) answers[key] = value;
  });

  return { answers, activeSectionIndex, countryCode };
};

const stableStringify = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (isPlainObject(value)) {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
};

/** Compares answers and country; section position and metadata are not meaningful edits. */
export const draftAnswersEqual = (a: DraftSnapshot | null | undefined, b: DraftSnapshot | null | undefined) => {
  if (!a || !b) return a === b;
  return a.countryCode === b.countryCode && stableStringify(a.answers) === stableStringify(b.answers);
};

/** Builds the SaveDraft `draftData`/`draftMetadata` Struct maps (plain JSON on the wire). */
export const toSaveDraftPayload = (
  snapshot: DraftSnapshot,
  metadata: DraftMetadata
): Pick<SaveDraftRequest, 'draftData' | 'draftMetadata'> => ({
  draftData: {
    answers: snapshot.answers,
    activeSectionIndex: snapshot.activeSectionIndex,
    ...(snapshot.countryCode && { countryCode: snapshot.countryCode }),
  } as unknown as SaveDraftRequest['draftData'],
  draftMetadata: { ...metadata } as unknown as SaveDraftRequest['draftMetadata'],
});

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const asOptionValues = (field: Pick<DynamicFormField, 'options'>) =>
  new Set(getFieldOptions(field.options).map((option) => option.value));

type SanitizedAnswer = { value: unknown; lossy: boolean } | undefined;

/** Validates a saved answer against the active field definition. `undefined` means the answer is dropped. */
const sanitizeAnswer = (field: Pick<DynamicFormField, 'meta' | 'options'>, value: unknown): SanitizedAnswer => {
  switch (field.meta.type) {
    case 'checklist':
    case 'multiselect-dropdown': {
      if (!isStringArray(value)) return undefined;
      const options = asOptionValues(field);
      if (!options.size) return { value, lossy: false };
      const kept = value.filter((item) => options.has(item));
      return kept.length ? { value: kept, lossy: kept.length !== value.length } : undefined;
    }

    case 'categorizedChecklist':
      return isStringArray(value) ? { value, lossy: false } : undefined;

    case 'radio':
    case 'select': {
      if (typeof value !== 'string') return undefined;
      const options = asOptionValues(field);
      return !options.size || options.has(value) ? { value, lossy: false } : undefined;
    }

    case 'imageUpload':
      return isStringArray(value) ? { value, lossy: false } : undefined;

    case 'cardCapture': {
      if (!isPlainObject(value)) return undefined;
      const { front, back } = value;
      if ((front !== undefined && typeof front !== 'string') || (back !== undefined && typeof back !== 'string')) {
        return undefined;
      }
      return { value: { front: front ?? '', back: back ?? '' }, lossy: false };
    }

    case 'cardOnFile':
    case 'achOnFile': {
      // Only a method reference (+ cardholder name for cards) is restorable; details are re-fetched (§B8.2).
      if (!isPlainObject(value) || typeof value.paymentMethodId !== 'string' || !value.paymentMethodId) {
        return undefined;
      }
      const nameOnCard =
        field.meta.type === 'cardOnFile' && typeof value.nameOnCard === 'string' ? value.nameOnCard : '';
      return {
        value:
          field.meta.type === 'cardOnFile'
            ? { paymentMethodId: value.paymentMethodId, nameOnCard }
            : { paymentMethodId: value.paymentMethodId },
        lossy: false,
      };
    }

    case 'address':
    case 'insuranceVerification':
      return isPlainObject(value) && Object.values(value).every((item) => typeof item !== 'object')
        ? { value, lossy: false }
        : undefined;

    case 'checkbox':
      return typeof value === 'boolean' ? { value, lossy: false } : undefined;

    case 'eSign': {
      if (!isPlainObject(value)) return undefined;
      // Same shape `captureDraftSnapshot` produces, so a restored answer doesn't read as an edit.
      const restored = normalizeAnswer(decodeFieldValue('eSign', value));
      return restored ? { value: restored, lossy: false } : undefined;
    }

    case 'pdf': {
      const restored = decodeFieldValue('pdf', value);
      return restored ? { value: restored, lossy: false } : undefined;
    }

    default:
      return typeof value === 'string' || typeof value === 'number' ? { value, lossy: false } : undefined;
  }
};

export const parseDraftCountryCode = (response: GetDraftResponse | undefined): CountryCode | undefined => {
  const rawData = response?.draftData as unknown;
  if (!isPlainObject(rawData)) return undefined;
  return ALLOWED_COUNTRY_CODES.find((code) => code === rawData.countryCode);
};

const DRAFT_ID_PATTERN = /^[A-Za-z0-9_-]{8,128}$/;

export type ParsedDraft =
  | { ok: true; metadata: DraftMetadata; snapshot: DraftSnapshot; droppedKeys: string[] }
  | { ok: false; reason: 'missing-draft-id' | 'context-mismatch' | 'malformed' };

/**
 * Validates a GetDraft response against the active form definition (spec §8.3). Rejects a missing/malformed
 * `draftId` or a draft bound to another submission; drops unknown or incompatible answers (partial restore).
 */
export const parseDraftResponse = (
  response: GetDraftResponse | undefined,
  { formFieldsMap, submissionId }: { formFieldsMap: DraftFieldsMap; submissionId: string }
): ParsedDraft => {
  const rawMetadata = (response?.draftMetadata ?? {}) as unknown as Record<string, unknown>;
  const rawData = response?.draftData as unknown;

  const draftId = rawMetadata.draftId;
  if (typeof draftId !== 'string' || !DRAFT_ID_PATTERN.test(draftId)) return { ok: false, reason: 'missing-draft-id' };
  if (typeof rawMetadata.submissionId === 'string' && rawMetadata.submissionId !== submissionId) {
    return { ok: false, reason: 'context-mismatch' };
  }
  if (!isPlainObject(rawData) || !isPlainObject(rawData.answers)) return { ok: false, reason: 'malformed' };

  const answers: Record<string, unknown> = {};
  const droppedKeys: string[] = [];

  Object.entries(rawData.answers).forEach(([key, rawValue]) => {
    if (!formFieldsMap[key]) {
      droppedKeys.push(key);
      return;
    }
    if (isExcludedField(key, formFieldsMap)) return;

    const sanitized = sanitizeAnswer(formFieldsMap[key], rawValue);
    if (!sanitized) {
      droppedKeys.push(key);
      return;
    }
    if (sanitized.lossy) droppedKeys.push(key);
    answers[key] = sanitized.value;
  });

  const rawIndex = rawData.activeSectionIndex;
  const activeSectionIndex = typeof rawIndex === 'number' && Number.isInteger(rawIndex) && rawIndex >= 0 ? rawIndex : 0;
  const version = typeof rawMetadata.version === 'number' && rawMetadata.version >= 0 ? rawMetadata.version : 0;

  return {
    ok: true,
    metadata: {
      draftId,
      version,
      submissionId,
      savedAt: typeof rawMetadata.savedAt === 'string' ? rawMetadata.savedAt : '',
      payloadVersion: DRAFT_PAYLOAD_VERSION,
    },
    snapshot: { answers, activeSectionIndex, countryCode: parseDraftCountryCode(response) },
    droppedKeys,
  };
};

export interface DraftMediaRef {
  /** Answer key that owns the media id. */
  answerKey: string;
  /** Attachment `fieldKey` in the dynamic-form store (`<key>` or `<key>.front|back` for card capture). */
  attachmentFieldKey: string;
  mediaId: string;
}

/** Lists the committed media ids referenced by restored answers. */
export const collectDraftMediaRefs = (answers: Record<string, unknown>, formFieldsMap: DraftFieldsMap) =>
  Object.entries(answers).flatMap(([answerKey, value]): DraftMediaRef[] => {
    const type = formFieldsMap[answerKey]?.meta.type;

    if (type === 'imageUpload' && isStringArray(value)) {
      return value.filter(Boolean).map((mediaId) => ({ answerKey, attachmentFieldKey: answerKey, mediaId }));
    }

    if (type === 'cardCapture' && isPlainObject(value)) {
      return (['front', 'back'] as const)
        .filter((side) => typeof value[side] === 'string' && value[side])
        .map((side) => ({ answerKey, attachmentFieldKey: `${answerKey}.${side}`, mediaId: value[side] as string }));
    }

    if (type === 'eSign' && isPlainObject(value)) {
      const signature = parseESignValue(value);
      return isImageESign(signature) && isPersistedMediaId(signature.data)
        ? [{ answerKey, attachmentFieldKey: answerKey, mediaId: signature.data }]
        : [];
    }

    const signedAttachmentId = type === 'pdf' ? getSignedAttachmentId(value) : '';
    if (isPersistedMediaId(signedAttachmentId)) {
      return [{ answerKey, attachmentFieldKey: answerKey, mediaId: signedAttachmentId }];
    }

    return [];
  });

/** Removes unavailable media ids from restored answers, so fields never present unavailable files as ready. */
export const removeDraftMediaIds = (
  answers: Record<string, unknown>,
  formFieldsMap: DraftFieldsMap,
  unavailable: Set<string>
): Record<string, unknown> => {
  if (!unavailable.size) return answers;

  return Object.fromEntries(
    Object.entries(answers).flatMap(([key, value]) => {
      const type = formFieldsMap[key]?.meta.type;
      if (type === 'imageUpload' && isStringArray(value)) {
        const kept = value.filter((id) => !unavailable.has(id));
        return kept.length ? [[key, kept]] : [];
      }
      if (type === 'cardCapture' && isPlainObject(value)) {
        const front = typeof value.front === 'string' && !unavailable.has(value.front) ? value.front : '';
        const back = typeof value.back === 'string' && !unavailable.has(value.back) ? value.back : '';
        return front || back ? [[key, { front, back }]] : [];
      }
      // A signature or signed PDF that can't be downloaded must be re-signed.
      if (type === 'eSign' && isPlainObject(value) && typeof value.data === 'string' && unavailable.has(value.data)) {
        return isImageESign(parseESignValue(value)) ? [] : [[key, value]];
      }
      if (type === 'pdf' && isPlainObject(value) && typeof value.signedAttachmentId === 'string') {
        return unavailable.has(value.signedAttachmentId) ? [] : [[key, value]];
      }
      return [[key, value]];
    })
  );
};
